/**
 * Redesign Welle R — ein schlanker RRULE-Selector für iterative Prozesse
 * (D-150). Erzeugt genau die Teilmenge, die der Server-Expander versteht
 * (`server/src/storage/rrule.js`): `FREQ=DAILY|WEEKLY|MONTHLY;INTERVAL=<n>[;BYDAY=MO,…]`.
 * BYDAY nur bei WEEKLY. Kein externes rrule-Paket — bewusst minimal.
 */

import { useMemo } from "react";

type Freq = "DAILY" | "WEEKLY" | "MONTHLY";

const FREQ_LABEL: Record<Freq, string> = {
  DAILY: "Täglich",
  WEEKLY: "Wöchentlich",
  MONTHLY: "Monatlich",
};
// Reihenfolge Mo…So (Anzeige); Werte = RRULE-Tokens.
const WEEKDAYS: { token: string; label: string }[] = [
  { token: "MO", label: "Mo" },
  { token: "TU", label: "Di" },
  { token: "WE", label: "Mi" },
  { token: "TH", label: "Do" },
  { token: "FR", label: "Fr" },
  { token: "SA", label: "Sa" },
  { token: "SU", label: "So" },
];

interface Parsed {
  freq: Freq;
  interval: number;
  byday: string[];
}

function parse(value: string): Parsed {
  const out: Parsed = { freq: "WEEKLY", interval: 1, byday: [] };
  for (const part of String(value || "").split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) {
      continue;
    }
    const key = part.slice(0, eq).trim().toUpperCase();
    const val = part.slice(eq + 1).trim();
    if (
      key === "FREQ" &&
      ["DAILY", "WEEKLY", "MONTHLY"].includes(val.toUpperCase())
    ) {
      out.freq = val.toUpperCase() as Freq;
    } else if (key === "INTERVAL") {
      out.interval = Math.max(1, parseInt(val, 10) || 1);
    } else if (key === "BYDAY") {
      out.byday = val
        .split(",")
        .map((x) => x.trim().toUpperCase())
        .filter((x) => WEEKDAYS.some((w) => w.token === x));
    }
  }
  return out;
}

function compose(p: Parsed): string {
  const parts = [`FREQ=${p.freq}`, `INTERVAL=${Math.max(1, p.interval)}`];
  if (p.freq === "WEEKLY" && p.byday.length) {
    parts.push(`BYDAY=${p.byday.join(",")}`);
  }
  return parts.join(";");
}

export const RRuleSelector = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (rrule: string) => void;
}) => {
  const p = useMemo(() => parse(value), [value]);
  const emit = (next: Partial<Parsed>) => onChange(compose({ ...p, ...next }));

  return (
    <div className="ryfe-rrule">
      <div className="ryfe-rrule__row">
        <span className="ryfe-rrule__pre">Alle</span>
        <input
          className="ryfe-rrule__interval"
          type="number"
          min={1}
          value={p.interval}
          onChange={(e) =>
            emit({ interval: Math.max(1, parseInt(e.target.value, 10) || 1) })
          }
        />
        <select
          value={p.freq}
          onChange={(e) => emit({ freq: e.target.value as Freq })}
        >
          {(["DAILY", "WEEKLY", "MONTHLY"] as Freq[]).map((f) => (
            <option key={f} value={f}>
              {FREQ_LABEL[f]}
            </option>
          ))}
        </select>
      </div>

      {p.freq === "WEEKLY" && (
        <div className="ryfe-rrule__days">
          {WEEKDAYS.map((w) => {
            const on = p.byday.includes(w.token);
            return (
              <button
                key={w.token}
                type="button"
                className={`ryfe-rrule__day${on ? " ryfe-rrule__day--on" : ""}`}
                aria-pressed={on}
                onClick={() =>
                  emit({
                    byday: on
                      ? p.byday.filter((d) => d !== w.token)
                      : [...p.byday, w.token],
                  })
                }
              >
                {w.label}
              </button>
            );
          })}
        </div>
      )}

      <code className="ryfe-rrule__preview">{compose(p)}</code>
    </div>
  );
};
